'use client'

import type { RouteModel } from '@polaris/core/models'
import { formatCoin, formatPrice } from '@polaris/core/utils'

import { useAtomValue } from 'jotai'
import { ArrowRight, CircleAlert, History } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useMemo } from 'react'
import { useUserSettings } from '@/src/hooks/use-user-settings'
import {
  errorAtom,
  isStepSigningAtom,
  isSuccessAtom,
  routeAtom,
  routeMachineSnapshotAtom,
  routeTimingAtom,
} from '@/src/stores/route-machine/atoms'
import { cn } from '@/src/utils/styles'
import { RadialProgress } from '../radial-progress'
import {
  ListRowButton,
  type ListRowButtonProps,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../ui/list-row'
import TokenImg from '../ui/token-img'

interface ActivityItemProps extends Omit<ListRowButtonProps, 'id' | 'type'> {
  route: RouteModel
}

export const ActivityItem = ({ route, ref, ...rest }: ActivityItemProps) => {
  const activeRoute = useAtomValue(routeAtom)
  const isSuccess = useAtomValue(isSuccessAtom)
  const error = useAtomValue(errorAtom)
  const isStepSigning = useAtomValue(isStepSigningAtom)
  const snapshot = useAtomValue(routeMachineSnapshotAtom)
  const pathname = usePathname()
  const { userSettings } = useUserSettings()
  // Compute timing for the currently selected route in store
  const timing = useAtomValue(routeTimingAtom)

  const id = route.ID
  const type = route.status.type
  const usdValue = formatPrice(route.usdValueAmountOut)
  const status = route.status.statusText
  const from = route.InputAssetMetadata
  const to = route.OutputAssetMetadata
  const refundTicker =
    activeRoute?.ID === id
      ? snapshot.context.refundInfo?.ticker
      : route.RouteRefundInfo?.ticker
  const amount = formatCoin({
    denom: route.OutputAssetMetadata.ticker,
    amount: route.AmountOut,
  })

  // Prefer live timing from atom only when it corresponds to this item.
  // Otherwise, fall back to route-level derived timing for historical items.
  const routeSelfTiming = route.timingMs
  const useAtomTiming = timing && timing.routeId === id
  const startMs = useAtomTiming ? timing!.startMs : routeSelfTiming?.startMs
  const endMs = useAtomTiming ? timing!.endMs : routeSelfTiming?.endMs

  // Type from when route is executing, not from history
  const internalType = useMemo(() => {
    if (activeRoute?.ID === id) {
      if (isSuccess) {
        return 'success'
      }

      if (snapshot.value === 'FundsRecoverySuccess' || activeRoute.isAborted) {
        return 'aborted'
      }

      if (error) {
        return 'failed'
      }

      if (isStepSigning) {
        return 'paused-approval'
      }

      if (type === 'unknown') {
        return 'pending'
      }
    }

    return type
  }, [id, activeRoute, type, isSuccess, error, isStepSigning, snapshot.value])

  const description = useMemo(() => {
    if (status === 'Aborted') return 'Recovered'
    if (status && status !== '') return status

    switch (internalType) {
      case 'pending': {
        if (
          activeRoute?.sourceChain.chain_id ===
          activeRoute?.destinationChain.chain_id
        ) {
          return `On ${activeRoute?.sourceChain.chain_name}`
        }
        return `${activeRoute?.sourceChain.chain_name} to ${activeRoute?.destinationChain?.chain_name}`
      }

      case 'aborted':
        return 'Recovered'
    }
  }, [
    internalType,
    activeRoute?.destinationChain.chain_id,
    activeRoute?.destinationChain?.chain_name,
    activeRoute?.sourceChain.chain_id,
    activeRoute?.sourceChain.chain_name,
    status,
  ])

  const actionDescription = useMemo(() => {
    if (internalType === 'paused-approval') {
      return <span className='text-link'>&#39;· Resume&#39;</span>
    }

    if (internalType === 'failed') {
      return '· Action required'
    }

    return ''
  }, [internalType])

  const isAborted = internalType === 'aborted' || status === 'Aborted'
  const shouldShowRefundReplacement = Boolean(
    isAborted && refundTicker && refundTicker !== to.ticker,
  )

  const LinkComponent = internalType === 'expired' ? 'div' : Link

  return (
    <ListRowButton {...rest} ref={ref} asChild size='md'>
      <LinkComponent
        replace={pathname.startsWith('/route')}
        href={`/route/${id}`}
      >
        <div className='flex items-center gap-3'>
          <div className='relative rounded-full size-10 flex items-center justify-center'>
            {internalType !== 'paused-approval' ? (
              <TokenImg
                src={to.logo_img_url}
                alt={to.ticker}
                width={internalType === 'pending' ? 24 : 40}
                height={internalType === 'pending' ? 24 : 40}
                className='rounded-full'
              />
            ) : (
              <div className='flex items-center justify-center gap-0.5'>
                <div className='min-w-1 h-3 rounded-full bg-link' />
                <div className='min-w-1 h-3 rounded-full bg-link' />
              </div>
            )}
            {internalType === 'pending' && startMs && endMs && (
              <div className='absolute inset-0 z-10'>
                <RadialProgress
                  animated
                  startTime={startMs}
                  endTime={endMs}
                  strokeWidth={1}
                  progressClasses='-scale-y-100'
                  circleClasses='text-accent-primary'
                />
              </div>
            )}
            {(internalType === 'failed' || internalType === 'aborted') && (
              <div className='absolute z-10 -bottom-1.5 -right-1.5 size-7 bg-background flex items-center justify-center rounded-full'>
                {internalType === 'failed' ? (
                  <CircleAlert className='text-danger-text size-5' />
                ) : (
                  <History className='text-tertiary size-5' />
                )}
              </div>
            )}
          </div>
          <ListRowSide>
            <ListRowSideName className='inline-flex items-center gap-1'>
              <span>{from.ticker}</span>
              <ArrowRight width={16} height={16} strokeWidth={3} />
              <span
                className={cn({
                  'line-through opacity-60': shouldShowRefundReplacement,
                })}
              >
                {to.ticker}
              </span>
              {shouldShowRefundReplacement ? <span>{refundTicker}</span> : null}
            </ListRowSideName>
            <ListRowSideDescription
              className={cn({
                'text-danger-text': internalType === 'failed',
              })}
            >
              {description} {actionDescription}
            </ListRowSideDescription>
          </ListRowSide>
        </div>
        {internalType !== 'failed' && internalType !== 'paused-approval' ? (
          <ListRowSide className='text-right'>
            <ListRowSideName
              className={cn('font-normal', {
                'h-6': userSettings.maskBalances,
              })}
            >
              {!userSettings.maskBalances ? (
                usdValue
              ) : (
                <span className='text-tertiary-100 text-3xl h-6 font-normal'>
                  *****
                </span>
              )}
            </ListRowSideName>
            <ListRowSideDescription className='h-4'>
              {!userSettings.maskBalances && amount}
            </ListRowSideDescription>
          </ListRowSide>
        ) : null}
      </LinkComponent>
    </ListRowButton>
  )
}

ActivityItem.displayName = 'ActivityItem'
